import * as React from 'react';
import { Label, Title } from '@patternfly/react-core';
import { Table, Tbody, Td, Th, Thead, Tr } from '@patternfly/react-table';
import { aggregate, isLimbo, stageOf } from '../../../../shared/riskregister/aggregate';
import { completedBase } from '../../../../shared/riskregister/filters';
import type { Payload, Row, ViewState } from '../../../../shared/riskregister/types';

interface Segment {
  label: string;
  value: number;
  color: string;
}

const FlowBar: React.FunctionComponent<{ label: string; segments: Segment[] }> = ({ label, segments }) => {
  const total = segments.reduce((sum, segment) => sum + segment.value, 0);
  const description = segments.map((segment) => `${segment.label}: ${segment.value}`).join(', ');

  return (
    <div className="risk-register__flow">
      <div className="risk-register__flow-bar" role="img" aria-label={`${label}. ${description || 'No items'}`}>
        {segments
          .filter((segment) => segment.value > 0)
          .map((segment) => (
            <span
              aria-hidden="true"
              key={segment.label}
              style={{
                width: `${(segment.value / Math.max(total, 1)) * 100}%`,
                backgroundColor: segment.color,
                minWidth: '0.25rem',
              }}
            />
          ))}
      </div>
      <div className="risk-register__flow-legend">
        {segments.map((segment) => (
          <span key={segment.label}>
            <span aria-hidden="true" className="risk-register__flow-key" style={{ backgroundColor: segment.color }} />
            {segment.label}: {segment.value}
          </span>
        ))}
      </div>
    </div>
  );
};

interface StatusGroup {
  status: string;
  rows: Row[];
}

function statusGroups(rows: Row[]): StatusGroup[] {
  const groups = new Map<string, Row[]>();
  rows
    .filter((row) => row.open)
    .forEach((row) => {
      const group = groups.get(row.status);
      if (group) group.push(row);
      else groups.set(row.status, [row]);
    });
  return Array.from(groups, ([status, groupRows]) => ({ status, rows: groupRows }));
}

export const BottlenecksPanel: React.FunctionComponent<{
  payload: Payload;
  state: ViewState;
}> = ({ payload, state }) => {
  const base = completedBase(payload.rows, state, payload);
  const open = base.filter((row) => row.open);
  const limbo = open.filter((row) => isLimbo(row, payload.stale_days));
  const statuses = statusGroups(base).sort((left, right) => {
    const leftAggregate = aggregate(left.rows, payload);
    const rightAggregate = aggregate(right.rows, payload);
    return (
      rightAggregate.overdue - leftAggregate.overdue ||
      rightAggregate.weighted - leftAggregate.weighted ||
      rightAggregate.maxidle - leftAggregate.maxidle ||
      left.status.localeCompare(right.status)
    );
  });

  const stageSegments: Segment[] = [
    {
      label: 'Completed',
      value: base.filter((row) => stageOf(row) === 'done').length,
      color: 'var(--pf-v5-global--success-color--100)',
    },
    {
      label: 'Not started',
      value: base.filter((row) => stageOf(row) === 'todo').length,
      color: 'var(--pf-v5-global--warning-color--100)',
    },
    {
      label: 'In progress',
      value: base.filter((row) => stageOf(row) === 'prog').length,
      color: 'var(--pf-v5-global--info-color--100)',
    },
  ];
  const ownerSegments: Segment[] = [
    {
      label: 'Assigned',
      value: open.filter((row) => !!row.assignee).length,
      color: 'var(--pf-v5-global--success-color--100)',
    },
    {
      label: 'Unassigned',
      value: open.filter((row) => !row.assignee).length,
      color: 'var(--pf-v5-global--danger-color--100)',
    },
  ];

  return (
    <div className="risk-register__panel-stack">
      <div>
        <Title headingLevel="h3" size="md">
          Stage flow
        </Title>
        <p>Every item in this selection, including completed work.</p>
        <FlowBar label="Stage flow" segments={stageSegments} />
      </div>
      <div>
        <Title headingLevel="h3" size="md">
          Owner flow
        </Title>
        <p>Open items only.</p>
        <FlowBar label="Owner flow" segments={ownerSegments} />
      </div>
      <div className="risk-register__callout risk-register__callout--danger">
        <Title headingLevel="h3" size="md">
          Limbo
        </Title>
        <p className="risk-register__callout-copy">
          <strong>{limbo.length}</strong> open item{limbo.length === 1 ? '' : 's'} are not started, unassigned, and idle
          for at least {payload.stale_days} days.
          {limbo.length > 0 && <> The oldest has been idle {Math.max(...limbo.map((row) => row.idle))} days.</>}
        </p>
      </div>
      <div>
        <Title headingLevel="h3" size="md">
          Status pile-up
        </Title>
        <div className="risk-register__table-wrap">
          <Table aria-label="Open items by status" variant="compact">
            <Thead>
              <Tr>
                <Th scope="col">Status</Th>
                <Th scope="col">Open</Th>
                <Th scope="col">Overdue</Th>
                <Th scope="col">Idle</Th>
                <Th scope="col">Weighted risk</Th>
                <Th scope="col">Oldest idle</Th>
              </Tr>
            </Thead>
            <Tbody>
              {statuses.map((status, index) => {
                const summary = aggregate(status.rows, payload);
                return (
                  <Tr key={status.status}>
                    <Td dataLabel="Status">
                      <Label color={index < 2 ? 'red' : 'grey'} isCompact>
                        {status.status}
                      </Label>
                    </Td>
                    <Td dataLabel="Open">{summary.open}</Td>
                    <Td dataLabel="Overdue">{summary.overdue}</Td>
                    <Td dataLabel="Idle">{summary.stale}</Td>
                    <Td dataLabel="Weighted risk">{summary.weighted}</Td>
                    <Td dataLabel="Oldest idle">{summary.maxidle}d</Td>
                  </Tr>
                );
              })}
            </Tbody>
          </Table>
        </div>
      </div>
      <p className="risk-register__note">
        These bottleneck views lift the open-only, due, and stage filters. Other filters, the platform area,
        accountability drill-down, and search still apply.
      </p>
    </div>
  );
};
